import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { append, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Checkout } from './models/checkout';
import { CheckoutsService } from './checkouts.service';
import { GetCheckouts, PostCheckout, PostRenewCheckout, PostReturnCheckout, SetCheckout } from './checkouts.actions';
import { LoadingModel } from '../../../_core/state/loading.model';

export type CheckoutsStateModel = LoadingModel & {
  checkouts: Checkout[];
};

export const initialState: CheckoutsStateModel = {
  checkouts: [],
  isLoading: false,
};

@State<CheckoutsStateModel>({
  name: 'checkouts',
  defaults: initialState,
})
@Injectable()
export class CheckoutsState {
  private readonly checkoutsService = inject(CheckoutsService);

  @Selector()
  static getCheckouts(state: CheckoutsStateModel) {
    return state.checkouts;
  }

  @Selector()
  static getIsLoading(state: CheckoutsStateModel) {
    return state.isLoading;
  }

  @Action(GetCheckouts)
  getCheckouts(context: StateContext<CheckoutsStateModel>): Observable<Checkout[]> {
    context.setState(patch<CheckoutsStateModel>({ isLoading: true }));
    return this.checkoutsService.getCheckouts().pipe(
      tap((checkouts) => {
        context.setState(patch<CheckoutsStateModel>({ checkouts: checkouts, isLoading: false }));
      })
    );
  }

  @Action(PostCheckout)
  postCheckout(context: StateContext<CheckoutsStateModel>, { recordId, from, issueDate, rhId }: PostCheckout) {
    return this.checkoutsService.postCheckout(recordId, from, issueDate, rhId).pipe(
      tap((checkout) => {
        context.setState(
          patch<CheckoutsStateModel>({
            checkouts: insertItem<Checkout>(checkout, 0),
          })
        );
      })
    );
  }

  @Action(PostRenewCheckout)
  postRenewCheckout(context: StateContext<CheckoutsStateModel>, { checkoutUpdated }: PostRenewCheckout) {
    return this.checkoutsService.postRenewCheckout(checkoutUpdated.id).pipe(
      tap((renewedCheckout: Checkout) => {
        context.setState(
          patch({
            checkouts: updateItem<Checkout>(
              (checkout: Checkout) => checkout.id === checkoutUpdated.id,
              (checkout: Checkout) => ({ ...checkout, ...renewedCheckout })
            ),
          })
        );
      })
    );
  }

  @Action(PostReturnCheckout)
  postReturnCheckout(context: StateContext<CheckoutsStateModel>, { checkoutId }: PostReturnCheckout) {
    return this.checkoutsService.postReturnCheckout(checkoutId).pipe(
      tap(() => {
        context.setState(
          patch<CheckoutsStateModel>({
            checkouts: removeItem<Checkout>((checkout) => checkout.id === checkoutId),
          })
        );
      })
    );
  }

  @Action(SetCheckout)
  setCheckout(context: StateContext<CheckoutsStateModel>, { checkoutUpdated }: SetCheckout) {
    context.setState(
      patch({
        checkouts: updateItem<Checkout>((checkout) => checkout.id === checkoutUpdated.id, checkoutUpdated),
      })
    );
  }
}
